import 'react';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import type {I31Page, I31ProcedureRow} from '@/pages/components/audit-program/i-section/constants';
import {I31_PAGE} from '@/pages/components/audit-program/i-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface I31CountViewProps {
    header: AuditProgramSectionVO;
    page: I31Page;
    refCode?: string;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {...cellBorder, fontWeight: 600, fontSize: '0.75rem', py: 0.75, px: 0.5};
const bodySx = {...cellBorder, py: 0.5, px: 0.5, verticalAlign: 'top' as const};

function ProcedureBlock({
    title,
    rows,
    values,
    onChange,
    fieldPrefix,
}: {
    title: string;
    rows: I31ProcedureRow[];
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
    fieldPrefix: string;
}) {
    return (
        <Box sx={{mb: 2}}>
            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 1}}>
                {title}
            </Typography>
            <TableContainer>
                <Table size="small" sx={{minWidth: 720, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col style={{width: 56}}/>
                        <col/>
                        <col style={{width: 220}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} colSpan={2}/>
                            <TableCell sx={headSx} align="center">Notes</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2" fontWeight={600}>{row.num}</Typography>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2" sx={{lineHeight: 1.4}}>{row.text}</Typography>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <InputField
                                        type="text"
                                        fullWidth
                                        compact
                                        value={values[`${fieldPrefix}.${row.id}.notes`] ?? ''}
                                        onChange={(e) => onChange(`${fieldPrefix}.${row.id}.notes`, e.target.value)}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

/** I3-1 — Physical inventory count memorandum / test count summary. */
export default function I31CountView({
    header,
    page,
    refCode = 'I3-1',
    values,
    onChange,
}: I31CountViewProps) {
    const labels = {...I31_PAGE.fieldLabels, ...(page.fieldLabels ?? {})};
    const objectives = page.objectives?.length ? page.objectives : I31_PAGE.objectives;
    const countProcedures = page.countProcedures?.length ? page.countProcedures : I31_PAGE.countProcedures;
    const auditTestProcedures = page.auditTestProcedures?.length
        ? page.auditTestProcedures
        : I31_PAGE.auditTestProcedures;

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a12"
                refCode={refCode}
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.3, mb: 1.5, mt: 0.5}}
            >
                {page.title}
            </Typography>

            <Stack spacing={1} sx={{mb: 2, maxWidth: 720}}>
                {([
                    ['countDate', labels.countDate],
                    ['locations', labels.locations],
                    ['auditTeam', labels.auditTeam],
                    ['clientPersonnel', labels.clientPersonnel],
                ] as const).map(([key, label]) => (
                    <Stack key={key} direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color="text.secondary" sx={{minWidth: 200, whiteSpace: 'nowrap'}}>
                            {label}
                        </Typography>
                        <InputField
                            type="text"
                            fullWidth
                            compact
                            value={values[key] ?? ''}
                            onChange={(e) => onChange(key, e.target.value)}
                        />
                    </Stack>
                ))}
            </Stack>

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.5}}>
                {page.objectivesTitle}
            </Typography>
            <Stack spacing={0.35} sx={{mb: 2}}>
                {objectives.map((obj, idx) => (
                    <Typography key={idx} variant="body2" sx={{pl: 1, lineHeight: 1.4}}>
                        – {obj}
                    </Typography>
                ))}
            </Stack>

            <ProcedureBlock
                title={page.countProceduresTitle || I31_PAGE.countProceduresTitle}
                rows={countProcedures}
                values={values}
                onChange={onChange}
                fieldPrefix="count"
            />
            <ProcedureBlock
                title={page.auditTestTitle || I31_PAGE.auditTestTitle}
                rows={auditTestProcedures}
                values={values}
                onChange={onChange}
                fieldPrefix="audit"
            />

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                {page.coverageTitle || I31_PAGE.coverageTitle}
            </Typography>
            <InputField
                type="text"
                fullWidth
                multiline
                minRows={3}
                value={values.coverageNotes ?? ''}
                onChange={(e) => onChange('coverageNotes', e.target.value)}
                sx={{mb: 2}}
            />

            <Typography variant="subtitle2" fontWeight={700} sx={{mb: 0.75}}>
                {page.resultsTitle || I31_PAGE.resultsTitle}
            </Typography>
            <InputField
                type="text"
                fullWidth
                compact
                value={values.results ?? ''}
                onChange={(e) => onChange('results', e.target.value)}
            />
        </Box>
    );
}
